/* eslint-disable react-hooks/exhaustive-deps*/
import React, { useState, useEffect, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import axios from 'axios';
import css from './Services.module.css';
import { coachingSpecialties } from '../../../../marketplace-custom-config';
import { Multiselect } from 'multiselect-react-dropdown';
import { notifyError, notifySuccess } from '../../../../components/HotToast/HotToastProvider';

const MEETING_TYPES = ['Remote', 'In Person', 'Retreat'];
const SERVICE_DELIVERY_TYPES = ['Individuals', 'Couple', 'Small group'];

// Mirrors the provider commission rule in server/api-util/lineItems.js:
// 10% of the price, or this flat minimum, whichever is higher.
const PROVIDER_COMMISSION_PERCENTAGE = 10;
const MIN_PROVIDER_COMMISSION = 10;

function Services(props) {
  const { onUpdateListing, listing, setTabIndex, focusField, currentUser } = props;
  const [inputs, setInputs] = useState({
    price: '0',
    per: 'hourly',
    typeOfService: [],
    meetingType: [],
    service: [],
    eventDates: '',
    sessions: '',
    durationLabel: '',
    outcomesText: '',
    inclusionsText: '',
    walkAwayItems: [],
    itinerary: [],
  });
  const [errors, setErrors] = useState([]);
  // Per-field error message, shown inline right under the offending
  // field (in addition to the summary list) — see validateFields.
  const [fieldErrors, setFieldErrors] = useState({});
  const priceInputRef = useRef(null);
  const typeOfServiceWrapRef = useRef(null);
  const meetingTypeWrapRef = useRef(null);
  const serviceWrapRef = useRef(null);
  // Shared by the focusField deep-link effect below and by validateFields'
  // scroll-to-first-invalid-field behavior, so both stay in sync.
  const FIELD_REFS = {
    price: priceInputRef,
    typeOfService: typeOfServiceWrapRef,
    meetingType: meetingTypeWrapRef,
    service: serviceWrapRef,
  };
  const scrollToField = fieldKey => {
    const target = FIELD_REFS[fieldKey];
    if (target && target.current) {
      target.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (typeof target.current.focus === 'function') {
        target.current.focus();
      }
    }
  };

  // Jump to and focus a specific field when arriving here because the
  // "Activate" check on a closed ad found it missing.
  useEffect(() => {
    if (!focusField) return;
    scrollToField(focusField);
  }, []);

  // AI listing-content generator ("Generate with AI" — hero highlights,
  // walk-away outcomes, day-by-day itinerary, what's included), Gemini-
  // backed via /api/ai/generate-listing-content. Mirrors Overview's
  // description assistant (see callDescriptionAssistant there).
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiAvailable, setAiAvailable] = useState(true);

  // Cheap, unauthenticated probe — hides the AI assistant entirely when
  // GEMINI_API_KEY isn't configured, instead of showing a button that's
  // guaranteed to fail. Fails closed on error.
  useEffect(() => {
    axios
      .get('/api/ai/generate-listing-content/status')
      .then(res => {
        if (!res.data || res.data.available !== true) {
          setAiAvailable(false);
        }
      })
      .catch(() => setAiAvailable(false));
  }, []);

  // Calls the Gemini-backed listing-content generator
  // (server/api/ai/generateListingContent.js) with whatever the coach has
  // already entered about themselves (profile) and this listing (title,
  // description, pricing), and fills all four "Listing page content"
  // fields at once. Overwrites any existing text in those fields, same as
  // Overview's "Optimize with AI" overwrites the description editor.
  const callGenerateListingContent = async () => {
    if (aiBusy) return;
    setAiBusy(true);
    setAiError(null);
    try {
      const profile = (currentUser && currentUser.attributes && currentUser.attributes.profile) || {};
      const profilePublicData = profile.publicData || {};
      const res = await axios.post('/api/ai/generate-listing-content', {
        displayName: profile.displayName || '',
        bio: profile.bio || '',
        tagline: profilePublicData.tagline || profilePublicData.jobTitle || '',
        methodologyName: profilePublicData.methodologyName || '',
        methodologyTagline: profilePublicData.methodologyTagline || '',
        specialties: profilePublicData.specialties || [],
        listingTitle: listing.attributes.title || '',
        listingDescription: listing.attributes.description || '',
        category: listing.attributes.publicData && listing.attributes.publicData.category,
        price: inputs.price,
        per: inputs.per,
        typeOfService: inputs.typeOfService,
        meetingType: inputs.meetingType,
        service: inputs.service,
        sessions: inputs.sessions,
        durationLabel: inputs.durationLabel,
      });
      const generated = res.data;
      if (generated) {
        setInputs(prev => ({
          ...prev,
          outcomesText: Array.isArray(generated.heroHighlights)
            ? generated.heroHighlights.join('\n')
            : prev.outcomesText,
          inclusionsText: Array.isArray(generated.inclusions)
            ? generated.inclusions.join('\n')
            : prev.inclusionsText,
          walkAwayItems: Array.isArray(generated.outcomes) && generated.outcomes.length
            ? generated.outcomes
            : prev.walkAwayItems,
          itinerary: Array.isArray(generated.itinerary) && generated.itinerary.length
            ? generated.itinerary
            : prev.itinerary,
        }));
      } else {
        setAiError('Could not generate content right now — please try again.');
      }
    } catch (err) {
      const status = err.response && err.response.status;
      if (status === 429) {
        const retryAfter = err.response.data && err.response.data.retryAfterSeconds;
        setAiError(
          retryAfter
            ? `You've hit the AI assist limit — try again in ${retryAfter}s.`
            : "You've hit the AI assist limit — try again shortly."
        );
      } else if (!err.response || status === 503) {
        // Endpoint unreachable or key missing/model overloaded — hide the
        // assistant rather than leaving a button up that will just keep
        // failing.
        setAiAvailable(false);
      } else {
        setAiError('Could not generate content right now — please try again.');
      }
    } finally {
      setAiBusy(false);
    }
  };

  // Theme-aware chip colour so selected entries stay visible even when
  // var(--marketplaceColorMid) doesn't resolve in the multiselect library's
  // inline style context.
  const isVCoachTheme =
    typeof document !== 'undefined' &&
    document.body.classList.contains('v-coach-theme');
  const chipBg = isVCoachTheme ? '#7C3AED' : '#0D9488';
  const dropdownStyle = {
    chips: {
      background: chipBg,
      color: '#FFFFFF',
      borderRadius: '999px',
      minHeight: '40px',
    },
    searchBox: {
      border: '1px solid #D1D5DB',
      borderRadius: '10px',
      minHeight: '52px',
    },
    option: { backgroundColor: 'white', color: '#666666' },
  };

  useEffect(() => {
    setInputs({
      ...inputs,
      price:
        listing.attributes.price && listing.attributes.price.amount
          ? listing.attributes.price.amount / 100
          : '0',
      per: listing.attributes.publicData.per || 'hourly',
      typeOfService: listing.attributes.publicData['type-of-service'] || [],
      meetingType: listing.attributes.publicData['meeting-type'] || [],
      service: listing.attributes.publicData['service-delivery-to'] || [],
      eventDates: listing.attributes.publicData['event-dates'] || '',
      sessions: listing.attributes.publicData.sessions
        ? String(listing.attributes.publicData.sessions)
        : '',
      durationLabel: listing.attributes.publicData.durationLabel || '',
      outcomesText: (listing.attributes.publicData.outcomes || []).join('\n'),
      inclusionsText: (listing.attributes.publicData.inclusions || []).join('\n'),
      walkAwayItems: listing.attributes.publicData.walkAwayItems || [],
      itinerary: listing.attributes.publicData.itinerary || [],
    });
  }, []);

  const updateWalkAwayItem = (index, field, value) => {
    const next = [...inputs.walkAwayItems];
    next[index] = { ...next[index], [field]: value };
    setInputs({ ...inputs, walkAwayItems: next });
  };
  const addWalkAwayItem = () => {
    if (inputs.walkAwayItems.length >= 4) return;
    setInputs({ ...inputs, walkAwayItems: [...inputs.walkAwayItems, { title: '', description: '' }] });
  };
  const removeWalkAwayItem = index => {
    setInputs({ ...inputs, walkAwayItems: inputs.walkAwayItems.filter((_, i) => i !== index) });
  };

  const updateItineraryItem = (index, field, value) => {
    const next = [...inputs.itinerary];
    next[index] = { ...next[index], [field]: value };
    setInputs({ ...inputs, itinerary: next });
  };
  const addItineraryItem = () => {
    setInputs({
      ...inputs,
      itinerary: [...inputs.itinerary, { day: `Day ${inputs.itinerary.length + 1}`, title: '', description: '' }],
    });
  };
  const removeItineraryItem = index => {
    setInputs({ ...inputs, itinerary: inputs.itinerary.filter((_, i) => i !== index) });
  };

  const validateFields = () => {
    let validationErrors = [];
    let newFieldErrors = {};
    let isValid = true;
    let firstInvalidField = null;
    const markInvalid = (field, message) => {
      validationErrors.push(message);
      newFieldErrors[field] = message;
      if (!firstInvalidField) firstInvalidField = field;
      isValid = false;
    };

    const regex = RegExp(/^[1-9]\d{0,4}(?:\.\d{1,4})?$/g);
    const { price, typeOfService, meetingType, service } = inputs;
    if (!regex.test(price)) {
      markInvalid('price', 'Price is invalid.');
    }

    if (typeOfService.length < 1) {
      markInvalid('typeOfService', 'Add the type of service.');
    }

    if (meetingType.length < 1) {
      markInvalid('meetingType', 'Select meeting type');
    }

    if (service.length < 1) {
      markInvalid('service', 'Select who you deliver the service to');
    }

    setErrors(validationErrors);
    setFieldErrors(newFieldErrors);

    // Jump straight to whichever required field the coach missed first.
    if (!isValid && firstInvalidField) {
      scrollToField(firstInvalidField);
    }

    return isValid;
  };

  // advance=true: validate, save, move to Gallery (the old "Continue").
  // advance=false: same validate + save, but stays on Services — the
  // listing already exists by the time this tab is reachable, so there's
  // no create-vs-update branching to worry about (unlike Overview's
  // first save).
  const onSaveServices = advance => {
    let tempObj = { publicData: {} };
    if (inputs.price) {
      let price = {};
      price.amount = parseInt(inputs.price) * 100;
      price.currency = 'USD';
      price._sdkType = 'money';
      tempObj.price = price;
    }

    if (inputs.typeOfService) {
      tempObj.publicData['type-of-service'] = inputs.typeOfService;
    }

    if (inputs.per) {
      tempObj.publicData.per = inputs.per;
    }

    if (inputs.meetingType) {
      tempObj.publicData['meeting-type'] = inputs.meetingType;
    }

    if (inputs.service) {
      tempObj.publicData['service-delivery-to'] = inputs.service;
    }

    // Optional — only date-bound offerings (retreats, cohorts) need this;
    // ongoing 1:1 coaching just leaves it blank.
    tempObj.publicData['event-dates'] = inputs.eventDates || '';

    if (inputs.sessions) {
      tempObj.publicData.sessions = parseInt(inputs.sessions, 10);
    }
    tempObj.publicData.durationLabel = inputs.durationLabel || '';
    tempObj.publicData.outcomes = inputs.outcomesText
      .split('\n')
      .map(s => s.trim())
      .filter(Boolean);
    tempObj.publicData.inclusions = inputs.inclusionsText
      .split('\n')
      .map(s => s.trim())
      .filter(Boolean);
    tempObj.publicData.walkAwayItems = inputs.walkAwayItems
      .map(item => ({ title: (item.title || '').trim(), description: (item.description || '').trim() }))
      .filter(item => item.title);
    tempObj.publicData.itinerary = inputs.itinerary
      .map(item => ({
        day: (item.day || '').trim(),
        title: (item.title || '').trim(),
        description: (item.description || '').trim(),
      }))
      .filter(item => item.title);

    if (validateFields()) {
      onUpdateListing('service', { ...tempObj, id: listing.id }).then(res => {
        setErrors([]);
        notifySuccess(advance ? 'Saved' : 'Draft saved');
        if (advance) {
          props.history.push(`/l/edit/${res.data.data.id.uuid}/edit/gallery`);
          setTabIndex(2);
        }
      });
    } else {
      notifyError('Has non valid inputs');
    }
  };

  const onSwitchClick = (state, key) => {
    if (inputs[state]) {
      if (inputs[state].includes(key)) {
        setInputs({
          ...inputs,
          [state]: inputs[state].filter(x => x !== key),
        });
      } else {
        let tempArray = [...inputs[state]];
        tempArray.push(key);
        setInputs({ ...inputs, [state]: tempArray });
      }
    }
  };

  const multiselectToState = e => {
    let tempArray = [];
    for (let index = 0; index < e.length; index++) {
      tempArray.push(e[index].key);
    }
    return tempArray;
  };

  const getMultiSelectedValues = name => {
    let tempArray = [];
    if (listing.attributes.publicData[name]) {
      for (let i = 0; i < listing.attributes.publicData[name].length; i++) {
        tempArray[i] = {
          key: listing.attributes.publicData[name][i],
          label: listing.attributes.publicData[name][i],
        };
      }
    }
    return tempArray;
  };

  return (
    <div className={css.wrapper}>
      <div className={css.pageHeading}>
        <h2 className={css.pageTitle}>Service details</h2>
        <p className={css.pageSubtitle}>Set your price and how clients can work with you.</p>
      </div>

      <div className={css.price}>
        <p className={css.label}>Price</p>
        <p className={css.hint}>Set your rate in USD. Choose "Per Hour" for hourly sessions or "Package" for a fixed program price.</p>
        <div className={css.priceRow}>
          <input
            ref={priceInputRef}
            type="number"
            className={`${css.priceInput} ${fieldErrors.price ? css.inputError : ''}`}
            value={inputs.price}
            placeholder="e.g. 150"
            onChange={e => setInputs({ ...inputs, price: e.target.value })}
          />
          <select
            className={css.perSelect}
            value={inputs.per}
            onChange={e => setInputs({ ...inputs, per: e.target.value })}
          >
            <option value="hourly">Per Hour</option>
            <option value="package">Package</option>
          </select>
        </div>
        {fieldErrors.price && <p className={css.fieldError}>{fieldErrors.price}</p>}
        <p className={css.feeNote}>
          Once someone makes an order, you will receive this amount minus{' '}
          <span className={css.link}>
            Viveka fee and a payment process fee (${Math.max(
              MIN_PROVIDER_COMMISSION,
              ((parseFloat(inputs.price) || 0) * PROVIDER_COMMISSION_PERCENTAGE) / 100
            ).toFixed(2)})
          </span>
        </p>
        <p className={css.hint}>Minimum fee is ${MIN_PROVIDER_COMMISSION}.</p>
      </div>

      <div className={css.price}>
        <p className={css.label}>Next dates (optional)</p>
        <p className={css.hint}>
          For a retreat, immersion, or cohort with a fixed start — e.g. "October 23-25, 2026".
          Leave blank for ongoing 1:1 coaching.
        </p>
        <input
          type="text"
          className={css.eventDatesInput}
          value={inputs.eventDates}
          placeholder="e.g. October 23-25, 2026"
          onChange={e => setInputs({ ...inputs, eventDates: e.target.value })}
        />
      </div>

      <div ref={typeOfServiceWrapRef} className={css.typeOfSearvice}>
        <p className={css.label}>Type of service</p>
        <p className={css.hint}>Select the coaching areas you specialise in. Clients can search by these on the marketplace.</p>
        <Multiselect
          onRemove={e => setInputs({ ...inputs, typeOfService: multiselectToState(e) })}
          onSelect={e => setInputs({ ...inputs, typeOfService: multiselectToState(e) })}
          style={dropdownStyle}
          options={coachingSpecialties}
          displayValue="label"
          selectedValues={getMultiSelectedValues('type-of-service')}
        />
        {fieldErrors.typeOfService && <p className={css.fieldError}>{fieldErrors.typeOfService}</p>}
      </div>

      <div ref={meetingTypeWrapRef} className={css.meetingType}>
        <p className={css.label}>Meeting type</p>
        <p className={css.hint}>How will you meet with clients? Select all that apply.</p>
        <div className={css.pillRow}>
          {MEETING_TYPES.map(type => {
            const selected = inputs.meetingType.includes(type);
            return (
              <button
                type="button"
                key={type}
                className={`${css.pill} ${selected ? css.pillSelected : ''}`}
                onClick={() => onSwitchClick('meetingType', type)}
              >
                {type}
              </button>
            );
          })}
        </div>
        {fieldErrors.meetingType && <p className={css.fieldError}>{fieldErrors.meetingType}</p>}
      </div>

      <div ref={serviceWrapRef} className={css.serviceTo}>
        <p className={css.label}>Service delivery to</p>
        <p className={css.hint}>Who is this service for? Select all audience types you work with.</p>
        <div className={css.pillRow}>
          {SERVICE_DELIVERY_TYPES.map(type => {
            const selected = inputs.service.includes(type);
            return (
              <button
                type="button"
                key={type}
                className={`${css.pill} ${selected ? css.pillSelected : ''}`}
                onClick={() => onSwitchClick('service', type)}
              >
                {type}
              </button>
            );
          })}
        </div>
        {fieldErrors.service && <p className={css.fieldError}>{fieldErrors.service}</p>}
      </div>

      <div className={css.pageHeading}>
        <h2 className={css.pageTitle}>Listing page content</h2>
        <p className={css.pageSubtitle}>
          Optional — fill these in to unlock the extra sections on your listing page (hero
          highlights, outcomes, day-by-day breakdown, what's included).
        </p>
      </div>

      {aiAvailable && (
        <div className={css.aiAssistant}>
          <p className={css.aiHint}>
            Let AI draft these four sections from your profile and what you've already entered
            about this listing — you can edit anything it writes.
          </p>
          <div className={css.aiButtons}>
            <button
              type="button"
              className={css.aiButton}
              disabled={aiBusy}
              onClick={() => callGenerateListingContent()}
            >
              {aiBusy ? 'Working…' : '✨ Generate with AI'}
            </button>
          </div>
          {aiError && <div className={css.aiError}>{aiError}</div>}
        </div>
      )}

      <div className={css.priceRow}>
        <div className={css.price} style={{ flex: 1 }}>
          <p className={css.label}>Sessions (optional)</p>
          <p className={css.hint}>Total number of sessions included, e.g. 6.</p>
          <input
            type="number"
            min="0"
            className={css.priceInput}
            value={inputs.sessions}
            placeholder="e.g. 6"
            onChange={e => setInputs({ ...inputs, sessions: e.target.value })}
          />
        </div>
        <div className={css.price} style={{ flex: 1 }}>
          <p className={css.label}>Duration label (optional)</p>
          <p className={css.hint}>Shown next to sessions on the listing page.</p>
          <input
            type="text"
            className={css.eventDatesInput}
            value={inputs.durationLabel}
            placeholder="e.g. 3 days"
            onChange={e => setInputs({ ...inputs, durationLabel: e.target.value })}
          />
        </div>
      </div>

      <div className={css.price}>
        <p className={css.label}>Hero highlights (optional)</p>
        <p className={css.hint}>
          One per line. Shown as checkmarked bullets in the hero — the "you'll recognise
          yourself here" moments.
        </p>
        <textarea
          className={css.textarea}
          rows={4}
          value={inputs.outcomesText}
          placeholder={
            'One per line, e.g.\nYou love each other, and you keep landing in different versions of the same conversation.'
          }
          onChange={e => setInputs({ ...inputs, outcomesText: e.target.value })}
        />
      </div>

      <div className={css.price}>
        <p className={css.label}>What you'll walk away with (optional)</p>
        <p className={css.hint}>Up to 4 outcome cards shown below the hero.</p>
        {inputs.walkAwayItems.map((item, i) => (
          <div key={i} className={css.rowCard}>
            <input
              type="text"
              className={css.rowInput}
              value={item.title}
              placeholder="Title, e.g. See the pattern"
              onChange={e => updateWalkAwayItem(i, 'title', e.target.value)}
            />
            <textarea
              className={css.rowTextarea}
              rows={2}
              value={item.description}
              placeholder="Description"
              onChange={e => updateWalkAwayItem(i, 'description', e.target.value)}
            />
            <button
              type="button"
              className={css.removeRowBtn}
              onClick={() => removeWalkAwayItem(i)}
            >
              Remove
            </button>
          </div>
        ))}
        {inputs.walkAwayItems.length < 4 && (
          <button type="button" className={css.addRowBtn} onClick={addWalkAwayItem}>
            + Add outcome
          </button>
        )}
      </div>

      <div className={css.price}>
        <p className={css.label}>What you'll experience (optional)</p>
        <p className={css.hint}>Day-by-day breakdown, for multi-day programs.</p>
        {inputs.itinerary.map((item, i) => (
          <div key={i} className={css.rowCard}>
            <input
              type="text"
              className={css.rowInput}
              value={item.day}
              placeholder="Day label, e.g. Day One"
              onChange={e => updateItineraryItem(i, 'day', e.target.value)}
            />
            <input
              type="text"
              className={css.rowInput}
              value={item.title}
              placeholder="Title, e.g. See"
              onChange={e => updateItineraryItem(i, 'title', e.target.value)}
            />
            <textarea
              className={css.rowTextarea}
              rows={2}
              value={item.description}
              placeholder="Description"
              onChange={e => updateItineraryItem(i, 'description', e.target.value)}
            />
            <button
              type="button"
              className={css.removeRowBtn}
              onClick={() => removeItineraryItem(i)}
            >
              Remove
            </button>
          </div>
        ))}
        <button type="button" className={css.addRowBtn} onClick={addItineraryItem}>
          + Add day
        </button>
      </div>

      <div className={css.price}>
        <p className={css.label}>What's included (optional)</p>
        <p className={css.hint}>One per line.</p>
        <textarea
          className={css.textarea}
          rows={4}
          value={inputs.inclusionsText}
          placeholder={'One per line, e.g.\nPre-experience assessment for each partner'}
          onChange={e => setInputs({ ...inputs, inclusionsText: e.target.value })}
        />
      </div>

      <div className={css.errors}>
        {errors.map((item, index) => (
          <p key={index} className={css.error}>
            {item}
          </p>
        ))}
      </div>

      <div className={css.footerBar}>
        <span className={css.footerStep}>Step 2 of 3 — Services</span>
        <div className={css.footerActions}>
          <button
            type="button"
            className={css.saveDraftButton}
            onClick={() => onSaveServices(false)}
          >
            Save draft
          </button>
          <button
            type="button"
            className={css.continueButton}
            onClick={() => onSaveServices(true)}
          >
            Continue <span aria-hidden="true">→</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default withRouter(Services);
